import { Injectable } from "@angular/core";
import { CalAngularService } from "@cvx/cal-angular";
import {
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
} from "@azure/msal-browser";
import { AuthProviderEnum, IAuthStrategy } from "@models/core/auth";
import { ICurrentUser } from "@models/user-management/user";
import { environment } from "../../../environments/environment";
import { AccessToken } from "@models/http/http-options";
import { firstValueFrom } from "rxjs";

/** Typed view of the MSAL internals we access through CalAngularService. */
interface MsalClientApplication {
  handleRedirectPromise(): Promise<AuthenticationResult | null>;
  logoutPopup(request: { account: AccountInfo }): Promise<void>;
}

interface CalAngularServiceInternal {
  cal: { clientApplication: MsalClientApplication };
}

@Injectable({ providedIn: "root" })
export class CalAuthService implements IAuthStrategy {
  private get msalClient(): MsalClientApplication | null {
    return (
      (this.calAngularService as unknown as CalAngularServiceInternal).cal
        ?.clientApplication ?? null
    );
  }

  constructor(private readonly calAngularService: CalAngularService) {}

  async getCurrentUser(): Promise<ICurrentUser | null> {
    await firstValueFrom(this.calAngularService.isUserSignedIn());
    const profile = this.calAngularService.cvxClaimsPrincipal;
    if (!profile) return null;
    return {
      id: profile.objectId,
      login: profile.userName,
      name: profile.name,
      permissionCodes: [],
      roleNames: profile.roles ?? [],
      homePageMenuItemUrl: "/my-requests",
    };
  }

  async isUserSignedIn(): Promise<boolean> {
    return firstValueFrom(this.calAngularService.isUserSignedIn());
  }

  async getToken(): Promise<string | undefined> {
    // CalClient fires handleRedirectPromise() without await in its constructor.
    // Calling getToken before it resolves causes MSAL to throw interaction_in_progress,
    // which makes CalClient fall back to acquireTokenPopup — showing an unwanted popup on reload.
    // handleRedirectPromise() is idempotent: resolves instantly once already settled.
    try {
      await this.msalClient?.handleRedirectPromise();
    } catch {
      // Not a redirect flow — safe to ignore.
    }

    const token = await firstValueFrom(
      this.calAngularService.getAADToken(environment.cal.apiScopes, true),
    );

    const accessToken =
      typeof token === "string"
        ? token
        : (token as AuthenticationResult | null)?.accessToken;

    if (accessToken) {
      localStorage.setItem(AccessToken, accessToken);
      localStorage.setItem("idp", AuthProviderEnum.AzureAD);
    }

    return accessToken ?? undefined;
  }

  async userInitiatedSignOut(): Promise<void> {
    const account = this.calAngularService.getAccount();
    if (!account) return;

    // logoutPopup() bypasses CalClient's own userInitiatedSignOut(), which is
    // where @cvx/cal clears its "cachedCvxClaimsPrincipal_v2" storage entry.
    // Without this, a stale principal (and stale roles) survives logout and
    // gets reused on the next login instead of being refetched.
    localStorage.removeItem("cachedCvxClaimsPrincipal_v2");
    sessionStorage.removeItem("cachedCvxClaimsPrincipal_v2");

    // logoutPopup() invalidates the AAD session in a small popup that closes automatically,
    // keeping the main page intact instead of redirecting to Microsoft's sign-out page.
    await this.msalClient?.logoutPopup({ account });
  }

  async getUserRoles(): Promise<string[] | undefined> {
    await firstValueFrom(this.calAngularService.isUserSignedIn());
    return this.calAngularService.cvxClaimsPrincipal?.roles;
  }
}
